import { Dispatch, SetStateAction, memo, useMemo } from 'react';
import { Badge, Grid } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { MRT_PaginationState } from 'mantine-react-table';
import moment from 'moment';
import Table from '../../../components/Table';
import { OutsourcingReportRow, buildOutsourcingReportColumnIds } from './reportRows';

const formatDate = (value?: string | Date) => {
  if (!value) return '-';
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : '-';
};

const statusColor = (status: string) => {
  switch (status) {
    case 'pending':
      return 'yellow';
    case 'in_process':
      return 'blue';
    case 'returned':
      return 'green';
    default:
      return 'gray';
  }
};

export default memo(function TableView({
  tableInfoData = [],
  totalRowsCount = 0,
  pages = 0,
  isError = false,
  isLoading = false,
  pagination,
  setPagination
}: {
  tableInfoData: OutsourcingReportRow[];
  totalRowsCount: number;
  pages: number;
  isError: boolean;
  isLoading: boolean;
  pagination: MRT_PaginationState;
  setPagination: Dispatch<SetStateAction<MRT_PaginationState>>;
}) {
  const { t } = useTranslation(['FieldName', 'ReportsPage']);
  const columns = useMemo(() => {
    const columnIds = buildOutsourcingReportColumnIds();
    return columnIds.map((columnId) => {
      switch (columnId) {
        case 'outsourcedFolio':
          return {
            id: columnId,
            accessorFn: (row: OutsourcingReportRow) => row.outsourcedFolio || '-',
            header: t('ReportsPage:outsourcingReport.headers.outsourcedFolio')
          };
        case 'orderFolio':
          return {
            id: columnId,
            accessorFn: (row: OutsourcingReportRow) => row.orderFolio || '-',
            header: t('ReportsPage:outsourcingReport.headers.orderFolio')
          };
        case 'partnerName':
          return {
            id: columnId,
            accessorFn: (row: OutsourcingReportRow) => row.partnerName || '-',
            header: t('ReportsPage:outsourcingReport.headers.partner')
          };
        case 'status':
          return {
            id: columnId,
            accessorFn: (row: OutsourcingReportRow) => row.status,
            header: t('ReportsPage:outsourcingReport.headers.status'),
            Cell: ({ cell }: any) => {
              const value = cell.getValue() || '';
              return (
                <Badge color={statusColor(value)} variant="light">
                  {t(`FieldName:outsourcedStatuses.${value}`, { defaultValue: value })}
                </Badge>
              );
            }
          };
        case 'productName':
          return {
            id: columnId,
            accessorFn: (row: OutsourcingReportRow) => row.productName || '-',
            header: t('ReportsPage:outsourcingReport.headers.product')
          };
        case 'sectionName':
          return {
            id: columnId,
            accessorFn: (row: OutsourcingReportRow) =>
              row.sectionName || t('ReportsPage:outsourcingReport.fallbacks.unsectioned'),
            header: t('ReportsPage:outsourcingReport.headers.section')
          };
        case 'quantity':
          return {
            id: columnId,
            accessorFn: (row: OutsourcingReportRow) => row.quantity,
            header: t('ReportsPage:outsourcingReport.headers.quantity')
          };
        case 'pieces':
          return {
            id: columnId,
            accessorFn: (row: OutsourcingReportRow) =>
              row.pieces === null || row.pieces === undefined ? '-' : row.pieces,
            header: t('ReportsPage:outsourcingReport.headers.pieces')
          };
        case 'sentDate':
          return {
            id: columnId,
            accessorFn: (row: OutsourcingReportRow) => formatDate(row.sentDate),
            header: t('ReportsPage:outsourcingReport.headers.sentDate')
          };
        case 'receivedDate':
          return {
            id: columnId,
            accessorFn: (row: OutsourcingReportRow) => formatDate(row.receivedDate),
            header: t('ReportsPage:outsourcingReport.headers.receivedDate')
          };
        case 'operationalNotes':
        default:
          return {
            id: columnId,
            accessorFn: (row: OutsourcingReportRow) => row.operationalNotes || '-',
            header: t('ReportsPage:outsourcingReport.headers.operationalNotes')
          };
      }
    });
  }, [t]);

  return (
    <Grid gutter="md">
      <Grid.Col span={12}>
        <Table
          tableColumns={columns}
          pagination={pagination}
          setPagination={setPagination}
          tableData={tableInfoData}
          isError={isError}
          paginationIsLoading={isLoading}
          numPages={pages}
          totalRowsCount={totalRowsCount}
        />
      </Grid.Col>
    </Grid>
  );
});
